import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClassroomDto, ClassroomSettingsDto } from '../api/types.js';
import { useApi } from '../api/ApiProvider.js';
import type { UpdateClassroomSettingsBody } from '../api/home.js';
import { RemovePageButton } from './pageActions.js';
import styles from './settingsForm.module.css';

export function NumberPageSettings({ classroom }: { classroom: ClassroomDto }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const classroomId = classroom.id;

  const settingsQuery = useQuery<ClassroomSettingsDto>({
    queryKey: ['classroom-settings', classroomId],
    queryFn: () => api.home.getSettings(classroomId),
  });

  const save = useMutation({
    mutationFn: (patch: UpdateClassroomSettingsBody) => api.home.patchSettings(classroomId, patch),
    onSuccess: (next) => {
      queryClient.setQueryData<ClassroomSettingsDto>(['classroom-settings', classroomId], next);
    },
  });

  const drawingEnabled = settingsQuery.data?.numberDrawingEnabled ?? true;

  return (
    <div className={`glass glass--solid ${styles.panel}`}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.heading}>Number</h2>
          <p className={styles.description}>
            Place value, expanded form, number modeling, currency, and number-neighbors. Most pieces
            are built or chosen directly on the page during morning meeting.
          </p>
        </div>
        <RemovePageButton />
      </header>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Writing in the boxes</h3>
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={drawingEnabled}
            disabled={settingsQuery.isLoading || save.isPending}
            onChange={(e) => save.mutate({ numberDrawingEnabled: e.target.checked })}
          />
          Let students write directly in the expanded-form and number-neighbor boxes
        </label>
        <p className={styles.hint}>
          Turn off if your board has its own pen overlay (e.g. Promethean ActivPanel annotation) —
          the boxes still show, but in-app drawing is disabled so the board&rsquo;s pen renders
          faster and more precisely.
        </p>
      </div>
    </div>
  );
}
